import * as React from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import type { Project } from "../types";

export type ProjectDetailsMember = {
  id: string;
  displayName: string;
  email: string;
  accessLabel: string;
  initials: string;
  isOwner: boolean;
  canManage: boolean;
};

export type ProjectDetailsPagination = {
  page: number;
  pages: number;
  total: number;
};

type ProjectDetailsSheetProps = {
  open: boolean;
  project: Project;
  isRTL: boolean;
  canEditProject: boolean;
  canManageMembers: boolean;
  isEditing: boolean;
  editName: string;
  editDescription: string;
  editError: string;
  editPending: boolean;
  deletePending: boolean;
  members: ProjectDetailsMember[];
  membersLoading: boolean;
  membersError: string;
  memberSearch: string;
  pagination: ProjectDetailsPagination;
  restoreFocusElement: HTMLElement | null;
  restoreFocusScope: string | null;
  suppressFocusRestore?: boolean;
  restoreAddMemberFocusRef?: React.MutableRefObject<boolean>;
  restoreAccessMemberFocusRef?: React.MutableRefObject<string | null>;
  restoreRemoveMemberFocusRef?: React.MutableRefObject<string | null>;
  onClose: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditNameChange: (value: string) => void;
  onEditDescriptionChange: (value: string) => void;
  onSubmitEdit: () => void;
  onMemberSearchChange: (value: string) => void;
  onRetryMembers: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onAddMember: () => void;
  onChangeMemberAccess: (memberId: string) => void;
  onRemoveMember: (memberId: string) => void;
  onDeleteProject: (triggerElement: HTMLButtonElement) => void;
};

const ProjectDetailsSheet = ({
  open,
  project,
  isRTL,
  canEditProject,
  canManageMembers,
  isEditing,
  editName,
  editDescription,
  editError,
  editPending,
  deletePending,
  members,
  membersLoading,
  membersError,
  memberSearch,
  pagination,
  restoreFocusElement,
  restoreFocusScope,
  suppressFocusRestore = false,
  restoreAddMemberFocusRef,
  restoreAccessMemberFocusRef,
  restoreRemoveMemberFocusRef,
  onClose,
  onStartEdit,
  onCancelEdit,
  onEditNameChange,
  onEditDescriptionChange,
  onSubmitEdit,
  onMemberSearchChange,
  onRetryMembers,
  onPreviousPage,
  onNextPage,
  onAddMember,
  onChangeMemberAccess,
  onRemoveMember,
  onDeleteProject,
}: ProjectDetailsSheetProps) => {
  const { t } = useTranslation();
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const addMemberButtonRef = React.useRef<HTMLButtonElement>(null);
  const memberActionRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const memberSearchRef = React.useRef<HTMLInputElement>(null);
  const titleId = React.useId();
  const editErrorId = React.useId();

  const preventPendingDismissal = React.useCallback(
    (event: Event) => {
      if (editPending) event.preventDefault();
    },
    [editPending],
  );

  const requestClose = React.useCallback(() => {
    if (!editPending) onClose();
  }, [editPending, onClose]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) requestClose();
      }}
    >
      <SheetContent
        side={isRTL ? "left" : "right"}
        dir={isRTL ? "rtl" : "ltr"}
        showCloseButton={false}
        overlayClassName="bg-transparent"
        className="top-[60px] h-[calc(100dvh-60px)] w-[min(500px,100vw)] max-w-none gap-0 p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          window.setTimeout(() => {
            if (restoreRemoveMemberFocusRef?.current) {
              const memberId = restoreRemoveMemberFocusRef.current;
              restoreRemoveMemberFocusRef.current = null;
              const trigger = memberActionRefs.current.get(memberId);
              if (trigger?.isConnected && !trigger.disabled && trigger.getAttribute("aria-disabled") !== "true") {
                trigger.focus();
              } else {
                (memberSearchRef.current ?? closeButtonRef.current)?.focus();
              }
              return;
            }
            if (restoreAccessMemberFocusRef?.current) {
              const memberId = restoreAccessMemberFocusRef.current;
              restoreAccessMemberFocusRef.current = null;
              const trigger = memberActionRefs.current.get(memberId);
              if (trigger?.isConnected && !trigger.disabled && trigger.getAttribute("aria-disabled") !== "true") {
                trigger.focus();
              } else {
                (memberSearchRef.current ?? closeButtonRef.current)?.focus();
              }
              return;
            }
            if (restoreAddMemberFocusRef?.current && addMemberButtonRef.current && canManageMembers) {
              restoreAddMemberFocusRef.current = false;
              addMemberButtonRef.current.focus();
              return;
            }
            (isEditing
              ? nameInputRef.current
              : closeButtonRef.current
            )?.focus();
          }, 0);
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!suppressFocusRestore) {
            window.setTimeout(() => {
              if (
                restoreFocusElement?.isConnected &&
                restoreFocusElement.dataset.projectScope === restoreFocusScope
              ) {
                restoreFocusElement.focus();
              }
            }, 0);
          }
        }}
        onEscapeKeyDown={preventPendingDismissal}
        onPointerDownOutside={preventPendingDismissal}
        onInteractOutside={preventPendingDismissal}
        data-testid="project-details-sheet"
      >
        <header className="flex h-14 shrink-0 items-center gap-2 px-4 pt-2">
          <SheetTitle className="min-w-0 flex-1 text-xs font-normal text-muted-foreground">
            {t("projects.projectDetails")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("projects.projectDetailsDescription", { name: project.name })}
          </SheetDescription>
          <Button
            ref={closeButtonRef}
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("common.close")}
            onClick={requestClose}
            disabled={editPending}
            className="size-6 text-muted-foreground"
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
          <section aria-labelledby={`${titleId}-project-info`}>
            <div className="flex min-h-[45px] items-center justify-between gap-3 py-3">
              <h2
                id={`${titleId}-project-info`}
                className="text-base font-medium text-foreground"
              >
                {t("projects.projectInfo")}
              </h2>
              {canEditProject && !isEditing ? (
                <Button type="button" size="sm" onClick={onStartEdit}>
                  <Pencil aria-hidden="true" />
                  {t("projects.editProject")}
                </Button>
              ) : canEditProject ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onCancelEdit}
                    disabled={editPending}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={onSubmitEdit}
                    disabled={!editName.trim() || editPending}
                  >
                    {editPending ? (
                      <LoaderCircle
                        className="animate-spin"
                        aria-hidden="true"
                      />
                    ) : null}
                    {t("common.saveChanges")}
                  </Button>
                </div>
              ) : null}
            </div>

            {editError ? (
              <p
                id={editErrorId}
                role="alert"
                className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {editError}
              </p>
            ) : null}

            <div className="space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor={`${titleId}-name`}
                  className="block text-xs font-medium text-muted-foreground"
                >
                  {t("projects.projectName")}
                </label>
                {isEditing ? (
                  <Input
                    ref={nameInputRef}
                    id={`${titleId}-name`}
                    required
                    value={editName}
                    onChange={(event) => onEditNameChange(event.target.value)}
                    disabled={editPending}
                    aria-invalid={!editName.trim()}
                    aria-describedby={editError ? editErrorId : undefined}
                    className="rounded-lg border-border bg-input"
                  />
                ) : (
                  <div
                    id={`${titleId}-name`}
                    className="min-h-9 rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground shadow-xs"
                  >
                    {project.name}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label
                  htmlFor={`${titleId}-description-field`}
                  className="block text-xs font-medium text-muted-foreground"
                >
                  {t("common.description")}
                </label>
                {isEditing ? (
                  <textarea
                    id={`${titleId}-description-field`}
                    value={editDescription}
                    onChange={(event) =>
                      onEditDescriptionChange(event.target.value)
                    }
                    disabled={editPending}
                    aria-describedby={editError ? editErrorId : undefined}
                    className="h-[109px] w-full resize-none rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                ) : (
                  <div
                    id={`${titleId}-description-field`}
                    className="min-h-[109px] whitespace-pre-wrap rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground shadow-xs"
                  >
                    {project.description ||
                      t("projects.noDescriptionAvailable")}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section aria-labelledby={`${titleId}-members`} className="mt-4">
            <div className="flex min-h-[45px] items-center justify-between gap-3 py-3">
              <h2
                id={`${titleId}-members`}
                className="text-base font-medium text-foreground"
              >
                {t("projects.members")}
              </h2>
              {canManageMembers ? (
                <Button ref={addMemberButtonRef} type="button" size="sm" onClick={onAddMember}>
                  <Plus aria-hidden="true" />
                  {t("projects.addTeamMember")}
                </Button>
              ) : null}
            </div>

            <Input
              type="search"
              ref={memberSearchRef}
              value={memberSearch}
              onChange={(event) => onMemberSearchChange(event.target.value)}
              placeholder={t("projects.searchMembers")}
              aria-label={t("projects.searchMembers")}
              className="mb-3 rounded-lg border-border bg-input"
            />

            <div aria-live="polite">
              {membersLoading ? (
                <div className="flex min-h-28 items-center justify-center gap-2 text-sm text-muted-foreground">
                  <LoaderCircle
                    className="size-5 animate-spin"
                    aria-hidden="true"
                  />
                  {t("projects.loadingTeamMembers")}
                </div>
              ) : membersError ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  <p role="alert">{membersError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRetryMembers}
                    className="mt-3"
                  >
                    {t("common.retry")}
                  </Button>
                </div>
              ) : members.length > 0 ? (
                <ul className="space-y-2" aria-label={t("projects.members")}>
                  {members.map((member) => (
                    <li
                      key={member.id}
                      className="flex min-h-[53px] items-center gap-2 rounded-md px-2 py-[5.5px]"
                    >
                      <span
                        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-medium text-primary"
                        aria-hidden="true"
                      >
                        {member.initials}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm leading-[1.5] tracking-[0.005em] text-foreground">
                          {member.displayName}
                          {member.isOwner
                            ? ` (${t("organizationMembers.owner")})`
                            : ""}
                        </p>
                        <p className="truncate text-sm leading-[1.5] tracking-[0.005em] text-muted-foreground">
                          {member.email}
                        </p>
                      </div>
                      {member.canManage ? (
                        <DropdownMenu dir={isRTL ? "rtl" : "ltr"}>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              ref={(element) => {
                                if (element) memberActionRefs.current.set(member.id, element);
                                else memberActionRefs.current.delete(member.id);
                              }}
                              aria-label={t("projects.memberActions", {
                                name: member.displayName,
                              })}
                              className="inline-flex h-10 max-w-[45%] shrink-0 items-center gap-1 rounded-lg bg-muted px-2 text-sm font-normal leading-[1.5] tracking-[0.005em] text-foreground shadow-xs outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
                            >
                              <span className="truncate">
                                {member.accessLabel}
                              </span>
                              <ChevronDown
                                className="size-4 shrink-0 text-muted-foreground"
                                aria-hidden="true"
                              />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            collisionPadding={8}
                            className="rounded-lg p-0.5"
                          >
                            <DropdownMenuItem
                              onSelect={() => onChangeMemberAccess(member.id)}
                              className="min-h-8 rounded-md px-2 py-[5.5px]"
                            >
                              <Pencil aria-hidden="true" />
                              {t("projects.changeAccessLevel")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => onRemoveMember(member.id)}
                              className="min-h-8 rounded-md px-2 py-[5.5px]"
                            >
                              <Trash2 aria-hidden="true" />
                              {t("projects.removeMember")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="inline-flex h-10 max-w-[45%] shrink-0 items-center rounded-lg bg-muted px-2 text-sm leading-[1.5] tracking-[0.005em] text-foreground shadow-xs">
                          <span className="truncate">{member.accessLabel}</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {memberSearch
                    ? t("projects.noMatchingMembers")
                    : t("projects.noTeamMembers")}
                </p>
              )}
            </div>

            {pagination.pages > 1 ? (
              <nav
                className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3"
                aria-label={t("projects.memberPagination")}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onPreviousPage}
                  disabled={pagination.page <= 1 || membersLoading}
                >
                  {isRTL ? (
                    <ChevronRight aria-hidden="true" />
                  ) : (
                    <ChevronLeft aria-hidden="true" />
                  )}
                  {t("common.previous")}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {t("projects.memberPage", {
                    page: pagination.page,
                    pages: pagination.pages,
                    total: pagination.total,
                  })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onNextPage}
                  disabled={
                    pagination.page >= pagination.pages || membersLoading
                  }
                >
                  {t("common.next")}
                  {isRTL ? (
                    <ChevronLeft aria-hidden="true" />
                  ) : (
                    <ChevronRight aria-hidden="true" />
                  )}
                </Button>
              </nav>
            ) : null}
          </section>
        </div>

        {canEditProject ? (
          <footer className="flex h-[68px] shrink-0 items-center border-t border-border px-4">
            <Button
              type="button"
              variant="destructive"
              onClick={(event) => onDeleteProject(event.currentTarget)}
              disabled={deletePending || editPending}
            >
              {deletePending ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 aria-hidden="true" />
              )}
              {t("projects.deleteProject")}
            </Button>
          </footer>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default ProjectDetailsSheet;
